/**
 * Commands the Actions palette can run, as plain data.
 *
 * The palette is a search box over this list. Keeping the catalogue here means
 * a new command is a new entry rather than another branch in the menu, and the
 * filter that decides what is offered for a given selection can be tested
 * without rendering anything.
 */

import {
  ACTION_PRIORITY_OPTIONS,
  visibleActionStatusOptions,
} from '../ICSConstants';
import { ASSIGNEE_KIND_LABEL, type AssigneeDirectory } from './actionEditModel';
import { assigneeGroups } from './actionEditModel';

export type ActionPaletteCommand = {
  id: string;
  group: string;
  label: string;
  /** Extra words the search should match. */
  keywords?: string;
  /**
   * What running this would write to every selected action, or null when the
   * command is not a field write (view switch, open a dialog).
   */
  patch?: Record<string, unknown> | null;
  kind: 'patch' | 'assign' | 'due' | 'handover' | 'view';
  view?: 'table' | 'kanban' | 'timeline';
};

export function filterPaletteCommands(
  commands: ActionPaletteCommand[],
  search: string
): ActionPaletteCommand[] {
  const term = search.trim().toLowerCase();
  if (!term) return commands;
  return commands.filter((command) =>
    [command.label, command.group, command.keywords ?? '']
      .join(' ')
      .toLowerCase()
      .includes(term)
  );
}

export function groupPaletteCommands(
  commands: ActionPaletteCommand[]
): { group: string; commands: ActionPaletteCommand[] }[] {
  const groups: { group: string; commands: ActionPaletteCommand[] }[] = [];
  commands.forEach((command) => {
    const existing = groups.find((entry) => entry.group === command.group);
    if (existing) existing.commands.push(command);
    else groups.push({ group: command.group, commands: [command] });
  });
  return groups;
}

/**
 * The catalogue, given who can be assigned and whether a handover is possible.
 *
 * Status and priority are always offered: they are the fixed vocabularies.
 * Assignees come from the same directory the table uses, so a name that is not
 * on the roster cannot appear here either.
 */
export function actionPaletteCommands(options: {
  directory: AssigneeDirectory;
  canHandover: boolean;
  hwcg?: boolean;
}): ActionPaletteCommand[] {
  const { directory, canHandover, hwcg = false } = options;

  const status: ActionPaletteCommand[] = visibleActionStatusOptions(hwcg).map(
    (option) => ({
      id: `status:${option.value}`,
      group: 'Status',
      label: `Set status to ${option.label}`,
      keywords: option.value,
      kind: 'patch',
      patch: { status: option.value },
    })
  );

  const priority: ActionPaletteCommand[] = ACTION_PRIORITY_OPTIONS.map(
    (option) => ({
      id: `priority:${option.value}`,
      group: 'Priority',
      label: `Set priority to ${option.label}`,
      keywords: option.value,
      kind: 'patch',
      patch: { priority: option.value },
    })
  );

  const assign: ActionPaletteCommand[] = assigneeGroups(directory).flatMap(
    (group) =>
      group.items.map((item) => ({
        id: `assign:${group.kind}:${item.value}`,
        group: 'Assign',
        label: `Assign to ${item.label}`,
        keywords: `${ASSIGNEE_KIND_LABEL[group.kind]} ${item.label}`,
        kind: 'assign' as const,
        patch: { __assignee: `${group.kind}:${item.value}` },
      }))
  );

  const views: ActionPaletteCommand[] = [
    {
      id: 'view:table',
      group: 'View',
      label: 'Show list',
      kind: 'view',
      view: 'table',
    },
    {
      id: 'view:kanban',
      group: 'View',
      label: 'Show board',
      kind: 'view',
      view: 'kanban',
    },
    {
      id: 'view:timeline',
      group: 'View',
      label: 'Show timeline',
      kind: 'view',
      view: 'timeline',
    },
  ];

  const extras: ActionPaletteCommand[] = [
    {
      id: 'due',
      group: 'Schedule',
      label: 'Set due date…',
      keywords: 'deadline target',
      kind: 'due',
    },
  ];

  if (canHandover) {
    extras.push({
      id: 'handover',
      group: 'Assign',
      label: 'Hand a position’s work to the incoming person…',
      keywords: 'shift handover reassign position',
      kind: 'handover',
    });
  }

  return [...status, ...priority, ...assign, ...views, ...extras];
}
